import { create } from 'zustand';

/**
 * Authentication store for managing user state and session
 *
 * This store handles:
 * - User profile information from Clerk
 * - Authentication status and loading states
 * - User registration status tracking
 * - Session management (login/logout)
 *
 * The store integrates with Clerk for authentication but provides
 * a clean abstraction layer for the rest of the application.
 */

/**
 * Clerk user interface
 *
 * Represents the user data structure from Clerk authentication service.
 * Includes profile information, email, and authentication metadata.
 */
export interface ClerkUser {
  /** Unique user identifier from Clerk */
  id: string;
  /** User's first name */
  firstName?: string | null;
  /** User's last name */
  lastName?: string | null;
  /** User's full name (computed by Clerk) */
  fullName?: string | null;
  /** URL to user's profile image */
  imageUrl?: string;
  /** Primary email address object */
  primaryEmailAddress?: {
    emailAddress: string;
  } | null;
  /** Account creation timestamp */
  createdAt?: string;
  /** Last sign-in timestamp */
  lastSignInAt?: string;
  /** Whether two-factor authentication is enabled */
  twoFactorEnabled?: boolean;
}

/**
 * Authentication state interface
 *
 * Defines the complete authentication state including user data,
 * loading states, registration status, and session management.
 */
interface AuthState {
  // ===== USER STATE =====
  /** Current authenticated user or null if not logged in */
  user: ClerkUser | null;
  /** Set the current user (typically called by Clerk integration) */
  setUser: (user: ClerkUser | null) => void;

  // ===== LOADING STATES =====
  /** Whether authentication is currently loading/checking */
  isLoading: boolean;
  /** Set the loading state */
  setIsLoading: (loading: boolean) => void;

  // ===== REGISTRATION STATUS =====
  /** Current registration status for the user */
  registrationStatus: 'pending' | 'approved' | 'rejected' | 'unknown';
  /** Set the registration status */
  setRegistrationStatus: (
    status: 'pending' | 'approved' | 'rejected' | 'unknown'
  ) => void;

  // ===== SESSION MANAGEMENT =====
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Set the authentication status */
  setIsAuthenticated: (authenticated: boolean) => void;

  // ===== AUTHENTICATION ACTIONS =====
  /** Complete login process with user data */
  login: (user: ClerkUser) => void;
  /** Complete logout process and clear user data */
  logout: () => void;
  /** Update user profile information */
  updateUserProfile: (updates: Partial<ClerkUser>) => void;
}

/**
 * Authentication store implementation
 *
 * Creates a Zustand store for authentication state management.
 * The store provides fresh state on every page load with no persistence.
 *
 * Key features:
 * - Integration with Clerk authentication
 * - User session data management
 * - Registration status tracking
 * - Clean login/logout actions
 * - Fresh state on every page load (no persistence)
 */
export const useAuthStore = create<AuthState>()((set, get) => ({
  // ===== USER STATE =====
  /** Current authenticated user or null if not logged in */
  user: null,
  /** Set the current user (typically called by Clerk integration) */
  setUser: (user) => set({ user }),

  // ===== LOADING STATES =====
  /** Whether authentication is currently loading/checking */
  isLoading: true,
  /** Set the loading state */
  setIsLoading: (isLoading) => set({ isLoading }),

  // ===== REGISTRATION STATUS =====
  /** Current registration status for the user */
  registrationStatus: 'unknown',
  /** Set the registration status */
  setRegistrationStatus: (registrationStatus) => set({ registrationStatus }),

  // ===== SESSION MANAGEMENT =====
  /** Whether the user is currently authenticated */
  isAuthenticated: false,
  /** Set the authentication status */
  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),

  // ===== AUTHENTICATION ACTIONS =====

  /**
   * Complete login process with user data
   *
   * Sets user data, marks as authenticated, and clears loading state.
   * This is typically called after successful Clerk authentication.
   *
   * @param user User data from Clerk
   */
  login: (user) =>
    set({
      user,
      isAuthenticated: true,
      isLoading: false,
    }),

  /**
   * Complete logout process and clear user data
   *
   * Clears all user data, marks as unauthenticated, and resets
   * registration status. This is typically called on Clerk sign-out.
   */
  logout: () =>
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      registrationStatus: 'unknown',
    }),

  /**
   * Update user profile information
   *
   * Merges new profile data with existing user data.
   * Useful for updating profile after user edits.
   *
   * @param updates Partial user data to merge
   */
  updateUserProfile: (updates) => {
    const currentUser = get().user;
    if (currentUser) {
      set({ user: { ...currentUser, ...updates } });
    }
  },
}));
